import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { clearUserScreenerId, patientScreenerStart } from "../../store/actions/account.action";
import { StateParams } from "../../store/reducers";
import {MixpanelContext} from "../../analytics/Mixpanel";
import { IPatientScreeningEligibilityResponse, VisitTypeCodes } from "../../models";

interface Value {
    hasPendingOrElapsedScreeners: boolean
    isEligibleForScreeningBasedOnStatus: boolean
    userId: string
    dob: Date
    eligibilityResult?: IPatientScreeningEligibilityResponse
    isV2Path?: boolean
    selectedProviderId?: string
    todayAppointmentId?: string
    selectedProviderEnabled?: boolean
    showProviderDropdown?: boolean
}

export const PatientScreenerStatusPill: React.FC<{ value: Value }> = ({ value }) => {
    const analytics = React.useContext(MixpanelContext);
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { userScreenerId, provider, loginSource } = useSelector((state: StateParams) => state.account)
    const [patientDob] = useState<Date>(value.dob)

    const isV2Path = value.isV2Path === true

    const patientIsEligible = isV2Path
        ? (value.eligibilityResult?.isEligibleForScreeningBasedOnStatus === true &&
           value.eligibilityResult?.hasPendingOrElapsedScreeners === true)
        : (value.isEligibleForScreeningBasedOnStatus && value.hasPendingOrElapsedScreeners)

    const isButtonDisabled = isV2Path
        ? (!patientIsEligible ||
           (value.showProviderDropdown === true && !value.selectedProviderId) ||
           value.selectedProviderEnabled === false)
        : !value.isEligibleForScreeningBasedOnStatus

    const goToDetails = useCallback(() => {
        analytics?.identify(value.userId)
        analytics?.track("start_admin_screening", { provider: provider?.slug, patientId: value.userId })
        dispatch(patientScreenerStart({
            userId: value.userId,
            practiceType: provider?.practiceType,
            visitType: VisitTypeCodes.GeneralScreeners,
            isInsuredByMedicare: true,
            loginSource,
            providerId: value.selectedProviderId || undefined,
            appointmentId: value.todayAppointmentId || undefined,
        }))
    }, [value, provider, loginSource, dispatch])

    useEffect(() => {
        if (userScreenerId && patientDob) {
            navigate('/adminLanguagePreference', {state: {dob: patientDob}})
        }
    }, [userScreenerId])

    // v1 path: hide button entirely when not eligible
    if (!isV2Path && !value.isEligibleForScreeningBasedOnStatus) {
        return null
    }

    return (
        <button
            className={`items-center px-2 py-2 border text-sm font-medium rounded-md border-white text-white ${isButtonDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange'}`}
            onClick={goToDetails}
            disabled={isButtonDisabled}
        >
            Start Screening
        </button>
    );
}
